/**
 * ValveControlPanel - Panel de control manual de válvulas
 * 
 * Permite:
 * - Encender válvula indefinidamente
 * - Encender válvula por X minutos (duración definida por usuario)
 * - Apagar válvula
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Droplets,
  Power,
  PowerOff,
  Timer,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { ApiValve } from '@/types/api';

interface ValveControlPanelProps {
  valve: ApiValve;
  isManualMode: boolean;
  onOpen: (duration?: number) => Promise<void>;
  onClose: () => Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
}

export function ValveControlPanel({
  valve,
  isManualMode,
  onOpen,
  onClose,
  isLoading = false,
  disabled = false,
}: ValveControlPanelProps) {
  const [showDurationInput, setShowDurationInput] = useState(false);
  const [duration, setDuration] = useState<number>(30);
  const [actionInProgress, setActionInProgress] = useState<'open' | 'close' | 'timed' | null>(null);

  const isOpen = valve.status === 'open';

  const handleOpen = async (withDuration: boolean = false) => {
    try {
      setActionInProgress(withDuration ? 'timed' : 'open');
      await onOpen(withDuration ? duration : undefined);
      setShowDurationInput(false);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleClose = async () => {
    try {
      setActionInProgress('close');
      await onClose();
    } finally {
      setActionInProgress(null);
    }
  };

  // Si no está en modo manual, mostrar solo el estado
  if (!isManualMode) {
    return (
      <div 
        className={cn(
          'flex items-center justify-between p-2 rounded-lg border',
          isOpen 
            ? 'border-success/30 bg-success/5' 
            : 'border-border bg-muted/30'
        )}
      >
        <div className="flex items-center gap-2">
          <Droplets className={cn(
            'w-4 h-4',
            isOpen ? 'text-success' : 'text-muted-foreground'
          )} />
          <span className="text-sm font-medium">{valve.name}</span>
        </div>
        <Badge 
          variant={isOpen ? 'default' : 'secondary'}
          className={cn('text-xs', isOpen && 'bg-success hover:bg-success/90')}
        >
          {isOpen ? 'Abierta' : 'Cerrada'}
        </Badge>
      </div>
    );
  }

  const isDisabled = disabled || isLoading || actionInProgress !== null;

  return (
    <div 
      className={cn(
        'p-3 rounded-lg border transition-all',
        isOpen 
          ? 'border-success/30 bg-success/5' 
          : 'border-border bg-card'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={cn(
            'p-1.5 rounded-md',
            isOpen ? 'bg-success/20' : 'bg-muted'
          )}>
            <Droplets className={cn(
              'w-4 h-4',
              isOpen ? 'text-success' : 'text-muted-foreground'
            )} />
          </div>
          <div>
            <span className="text-sm font-medium">{valve.name}</span>
            <p className="text-xs text-muted-foreground">Canal {valve.channel}</p>
          </div>
        </div>
        <Badge 
          variant={isOpen ? 'default' : 'outline'}
          className={cn(
            'transition-colors',
            isOpen ? 'bg-success hover:bg-success/90' : ''
          )}
        >
          {isOpen ? 'Abierta' : 'Cerrada'}
        </Badge>
      </div>

      {/* Control buttons */}
      <div className="flex gap-2">
        {/* Botón de encender */}
        {!isOpen && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpen(false)}
                  disabled={isDisabled}
                  className={cn(
                    'flex-1 gap-1.5',
                    'border-success/50 text-success hover:bg-success hover:text-success-foreground'
                  )}
                >
                  {actionInProgress === 'open' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Power className="w-3.5 h-3.5" />
                  )}
                  Encender
                </Button>
              </TooltipTrigger>
              <TooltipContent>Encender válvula indefinidamente</TooltipContent>
            </Tooltip>

            <Popover open={showDurationInput} onOpenChange={setShowDurationInput}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isDisabled}
                  className="gap-1.5"
                >
                  <Timer className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Tiempo</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="end">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm">Encender por tiempo</h4>
                    <p className="text-xs text-muted-foreground">
                      Define la duración en minutos (máx. 1440 = 24h)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={1440}
                      value={duration}
                      onChange={(e) => setDuration(Math.min(1440, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-24"
                      placeholder="30"
                    />
                    <span className="flex items-center text-sm text-muted-foreground">min</span>
                  </div>
                  {/* Quick duration buttons */}
                  <div className="flex gap-1 flex-wrap">
                    {[5, 10, 15, 30, 60].map((min) => (
                      <Button
                        key={min}
                        size="sm"
                        variant={duration === min ? 'default' : 'outline'}
                        className="h-7 px-2 text-xs"
                        onClick={() => setDuration(min)}
                      >
                        {min}m
                      </Button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="w-full gap-1.5 bg-success hover:bg-success/90"
                    onClick={() => handleOpen(true)}
                    disabled={actionInProgress !== null}
                  >
                    {actionInProgress === 'timed' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Power className="w-3.5 h-3.5" />
                    )}
                    Encender {duration} min
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}

        {/* Botón de apagar */}
        {isOpen && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleClose}
            disabled={isDisabled}
            className={cn(
              'flex-1 gap-1.5',
              'border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground'
            )}
          >
            {actionInProgress === 'close' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PowerOff className="w-3.5 h-3.5" />
            )}
            Apagar
          </Button>
        )}
      </div>

      {/* Modo manual indicator */}
      <div className="mt-3 pt-2 border-t flex items-center gap-1.5 text-xs text-muted-foreground">
        <AlertCircle className="w-3 h-3" />
        <span>Modo manual activo</span>
      </div>
    </div>
  );
}

/**
 * Compact version for card display with duration options
 * Soporta estados pendientes cuando se espera confirmación del backend
 */

interface ValveControlCompactProps {
  valve: ApiValve;
  isManualMode: boolean;
  onOpen: (duration?: number) => Promise<void>;
  onClose: () => Promise<void>;
  isLoading?: boolean;
  // Estado pendiente: indica que se envió un comando y esperamos confirmación
  pendingAction?: 'open' | 'close' | null;
  // Estado anterior antes del comando (para mostrar qué acción esperamos)
  expectedStatus?: 'open' | 'closed';
}

export function ValveControlCompact({
  valve,
  isManualMode,
  onOpen,
  onClose,
  isLoading = false,
  pendingAction = null,
  expectedStatus,
}: ValveControlCompactProps) {
  const [actionInProgress, setActionInProgress] = useState<'open' | 'close' | 'timed' | null>(null);
  const [showDurationPopover, setShowDurationPopover] = useState(false);
  const [duration, setDuration] = useState<number>(30);
  
  const isOpen = valve.status === 'open';
  const isPending = pendingAction !== null;
  const isDisabled = isLoading || actionInProgress !== null || isPending;

  // Handle open without duration (indefinite)
  const handleOpenIndefinite = async () => {
    try {
      setActionInProgress('open');
      await onOpen();
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle open with duration
  const handleOpenWithDuration = async () => {
    try {
      setActionInProgress('timed');
      await onOpen(duration);
      setShowDurationPopover(false);
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle close
  const handleClose = async () => {
    try {
      setActionInProgress('close');
      await onClose();
    } finally {
      setActionInProgress(null);
    }
  };

  // Determinar el color del borde según el estado
  const getBorderClass = () => {
    if (isPending) return 'border-amber-500/50 bg-amber-500/5';
    if (isOpen) return 'border-success/30 bg-success/5';
    return 'border-border bg-muted/30';
  };

  // Renderizar el badge de estado
  const renderStatusBadge = () => {
    if (isPending) {
      return (
        <Badge 
          variant="outline"
          className="text-xs border-amber-500/50 text-amber-600 gap-1"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          {pendingAction === 'open' ? 'Abriendo...' : 'Cerrando...'}
        </Badge>
      );
    }
    
    return (
      <Badge 
        variant={isOpen ? 'default' : 'secondary'}
        className={cn('text-xs', isOpen && 'bg-success hover:bg-success/90')}
      >
        {isOpen ? 'Abierta' : 'Cerrada'}
      </Badge>
    );
  };

  return (
    <div 
      className={cn(
        'flex items-center justify-between p-2.5 rounded-lg border transition-all',
        getBorderClass()
      )}
    >
      <div className="flex items-center gap-2">
        <Droplets className={cn(
          'w-4 h-4',
          isPending ? 'text-amber-500' : (isOpen ? 'text-success' : 'text-muted-foreground')
        )} />
        <div>
          <span className="text-sm font-medium">{valve.name}</span>
          <span className="text-xs text-muted-foreground ml-1.5">CH{valve.channel}</span>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {renderStatusBadge()}
        
        {isManualMode && !isPending && (
          <div className="flex items-center gap-1">
            {!isOpen ? (
              <>
                {/* Botón encender permanente */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 w-7 p-0 border-success/50 text-success hover:bg-success hover:text-white"
                      onClick={handleOpenIndefinite}
                      disabled={isDisabled}
                    >
                      {actionInProgress === 'open' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Power className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Encender (permanente)</TooltipContent>
                </Tooltip>

                {/* Botón encender con tiempo */}
                <Popover open={showDurationPopover} onOpenChange={setShowDurationPopover}>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs gap-1"
                      disabled={isDisabled}
                    >
                      <Timer className="w-3 h-3" />
                      <span className="hidden sm:inline">Tiempo</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56" align="end">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Encender por tiempo</p>
                      <div className="flex gap-1 flex-wrap">
                        {[5, 10, 15, 30, 60].map((min) => (
                          <Button
                            key={min}
                            size="sm"
                            variant={duration === min ? 'default' : 'outline'}
                            className="h-7 px-2 text-xs"
                            onClick={() => setDuration(min)}
                          >
                            {min}m
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={1440}
                          value={duration}
                          onChange={(e) => setDuration(Math.min(1440, Math.max(1, parseInt(e.target.value) || 1)))}
                          className="h-8 w-20 text-xs"
                        />
                        <span className="flex items-center text-xs text-muted-foreground">min</span>
                      </div>
                      <Button
                        size="sm"
                        className="w-full gap-1.5 bg-success hover:bg-success/90 text-white"
                        onClick={handleOpenWithDuration}
                        disabled={actionInProgress !== null}
                      >
                        {actionInProgress === 'timed' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Power className="w-3.5 h-3.5" />
                        )}
                        Encender {duration} min
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            ) : (
              /* Botón apagar */
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 gap-1 border-destructive/50 text-destructive hover:bg-destructive hover:text-white"
                    onClick={handleClose}
                    disabled={isDisabled}
                  >
                    {actionInProgress === 'close' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <PowerOff className="w-3.5 h-3.5" />
                    )}
                    <span>Apagar</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Apagar válvula</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        
        {/* Mostrar indicador cuando está pendiente */}
        {isPending && (
          <Tooltip>
            <TooltipTrigger>
              <AlertCircle className="w-4 h-4 text-amber-500" />
            </TooltipTrigger>
            <TooltipContent>
              Esperando confirmación del dispositivo...
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default ValveControlPanel;
